import MUIDataTable from 'mui-datatables'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { useActionRunner } from 'src/modules/notification'
import { Container } from 'src/modules/ui'
import { formatDateSearch } from 'src/utils'

import { useCustomers } from '../hooks'

export const CustomerAgreementList = ({
  customerAgreements,
  section,
  title,
}) => {
  const history = useHistory()

  const statusMap = {
    requested: 'Proposal Requested',
    proposed: 'Proposal Submitted',
    accepted: 'Proposal Accepted',
  }

  const data = customerAgreements.map(
    ({ statusChangedAt, createdAt, buyer, status, ...customerAgreement }) => ({
      ...customerAgreement,
      statusChangedAt: statusChangedAt && formatDateSearch(statusChangedAt),
      createdAt: formatDateSearch(createdAt),
      primaryContact: buyer.primaryContactName,
      buyer: buyer.name,
      status: statusMap[status],
    })
  )

  const columns = [
    {
      name: 'id',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: 'buyer',
      label: 'Customer',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'primaryContact',
      label: 'Primary Contact',
      options: {
        filter: false,
        sort: true,
      },
    },
  ]

  if (section === 'proposals') {
    columns.push(
      ...[
        {
          name: 'status',
          label: 'Status',
          options: {
            filter: true,
            sort: true,
          },
        },
        {
          name: 'createdAt',
          label: 'Date Requested',
          options: {
            filter: false,
            sort: true,
          },
        },
      ]
    )
  } else if (section === 'accepted') {
    columns.push({
      name: 'statusChangedAt',
      label: 'Date Accepted',
      options: {
        filter: false,
        sort: true,
      },
    })
  }

  const options = {
    download: false,
    elevation: 0,
    onRowClick: row => {
      history.push(`/customers/${row[0]}`)
    },
    print: false,
    responsive: 'simple',
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    selectableRows: 'none',
    viewColumns: false,
  }

  return (
    <Container muiTable>
      <MUIDataTable
        columns={columns}
        data={data}
        options={options}
        title={title}
      />
    </Container>
  )
}

CustomerAgreementList.propsTypes = {
  customerAgreements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      buyer: PropTypes.object,
      createdAt: PropTypes.string,
      price: PropTypes.string,
      status: PropTypes.string,
    })
  ),
  section: PropTypes.oneOf(['proposals', 'accepted']),
  title: PropTypes.string,
}

const CustomerAgreementListContainer = ({ section, title }) => {
  const { customerAgreements, fetchCustomerAgreements } = useCustomers()
  const [doFetchCustomerAgreements, isFetching] = useActionRunner(
    fetchCustomerAgreements,
    {
      initialFetchingState: true,
    }
  )

  useEffect(() => {
    doFetchCustomerAgreements(section)
  }, [])

  if (isFetching) {
    return null
  }

  return (
    <CustomerAgreementList
      customerAgreements={customerAgreements}
      section={section}
      title={title}
    />
  )
}

CustomerAgreementListContainer.propsTypes = {
  section: PropTypes.oneOf(['proposals', 'accepted']),
  title: PropTypes.string,
}

export default CustomerAgreementListContainer
